import React, { useState, useEffect } from 'react';
import { FaDollarSign, FaGamepad, FaTimes, FaShieldAlt, FaArrowLeft } from 'react-icons/fa';
import './PurchaseTypeModal.css';

interface PurchaseTypeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (isIngame: boolean, insurance?: string) => void;
  shipName: string;
}

export const PurchaseTypeModal: React.FC<PurchaseTypeModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  shipName
}) => {
  const [step, setStep] = useState<'purchase' | 'insurance'>('purchase');
  const [insuranceType, setInsuranceType] = useState<'LTI' | 'months'>('LTI');
  const [insuranceMonths, setInsuranceMonths] = useState<string>('');

  // Reset modal state when opening
  useEffect(() => {
    if (isOpen) {
      setStep('purchase');
      setInsuranceType('LTI');
      setInsuranceMonths('');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handlePurchaseTypeSelect = (isIngame: boolean) => {
    if (isIngame) {
      // Si es in-game, confirmar directamente
      onConfirm(true);
    } else {
      // Si es dinero, ir al paso de seguro
      setStep('insurance');
    }
  };

  const handleInsuranceConfirm = () => {
    const insurance = insuranceType === 'LTI' ? 'LTI' : `${insuranceMonths} meses`;
    onConfirm(false, insurance);
  };

  const canConfirmInsurance = insuranceType === 'LTI' || (insuranceType === 'months' && insuranceMonths.trim() !== '' && !isNaN(Number(insuranceMonths)));

  return (
    <div className="purchase-modal-backdrop" onClick={handleBackdropClick}>
      <div className="purchase-modal">
        <div className="purchase-modal-header">
          <div className="purchase-modal-title">
            {step === 'insurance' && (
              <button className="purchase-modal-back" onClick={() => setStep('purchase')}>
                <FaArrowLeft />
              </button>
            )}
            <h3>
              {step === 'purchase' ? '¿Cómo obtuviste la nave?' : 'Tiempo de seguro'}
            </h3>
          </div>
          <button className="purchase-modal-close" onClick={onClose}>
            <FaTimes />
          </button>
        </div>
        
        <div className="purchase-modal-content">
          {step === 'purchase' ? (
            <>
              <p>Indica cómo obtuviste <strong>{shipName}</strong>:</p>
              
              <div className="purchase-options">
                <button
                  className="purchase-option real-money"
                  onClick={() => handlePurchaseTypeSelect(false)}
                  data-tutorial-target="purchase-dinero-option"
                >
                  <div className="purchase-option-icon">
                    <FaDollarSign />
                  </div>
                  <div className="purchase-option-content">
                    <h4>Dinero / Store Credits</h4>
                    <p>Comprada con dinero real o créditos de la tienda</p>
                  </div>
                </button>

                <button
                  className="purchase-option in-game"
                  onClick={() => handlePurchaseTypeSelect(true)}
                >
                  <div className="purchase-option-icon">
                    <FaGamepad />
                  </div>
                  <div className="purchase-option-content">
                    <h4>In-Game</h4>
                    <p>Obtenida jugando (aUEC, alquiler, préstamo, etc.)</p>
                  </div>
                </button>
              </div>
            </>
          ) : (
            <>
              <p>Indica el tiempo de seguro de <strong>{shipName}</strong>:</p>
              
              <div className="insurance-options">
                <div className="insurance-option-group">
                  <label 
                    className={`insurance-option ${insuranceType === 'LTI' ? 'selected' : ''}`}
                    data-tutorial-target="insurance-lti-option"
                  >
                    <input
                      type="radio"
                      name="insurance"
                      value="LTI"
                      checked={insuranceType === 'LTI'}
                      onChange={() => setInsuranceType('LTI')}
                    />
                    <div className="insurance-option-content">
                      <div className="insurance-option-icon">
                        <FaShieldAlt />
                      </div>
                      <div>
                        <h4>LTI</h4>
                        <p>Lifetime Insurance</p>
                      </div>
                    </div>
                  </label>

                  <label className={`insurance-option ${insuranceType === 'months' ? 'selected' : ''}`}>
                    <input
                      type="radio"
                      name="insurance"
                      value="months"
                      checked={insuranceType === 'months'}
                      onChange={() => setInsuranceType('months')}
                    />
                    <div className="insurance-option-content">
                      <div className="insurance-option-icon">
                        <FaShieldAlt />
                      </div>
                      <div>
                        <h4>Tiempo específico</h4>
                        <p>Número de meses de seguro</p>
                      </div>
                    </div>
                  </label>
                </div>

                {insuranceType === 'months' && (
                  <div className="months-input-group">
                    <input
                      type="number"
                      min="1"
                      max="240"
                      value={insuranceMonths}
                      onChange={(e) => setInsuranceMonths(e.target.value)}
                      placeholder="Número de meses"
                      className="months-input"
                    />
                    <span className="months-label">meses</span>
                  </div>
                )}
              </div>

              <div className="insurance-actions">
                <button
                  className="insurance-confirm-btn"
                  onClick={handleInsuranceConfirm}
                  disabled={!canConfirmInsurance}
                  data-tutorial-target="insurance-confirm-button"
                >
                  Confirmar
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
